import { Calendar } from 'lucide-react';

import { PreviousProductCard } from '@/components/PreviousRecommendationsModal/PreviousRecommendationsModal';
import { Button } from '@/components/ui/button';
import type { PreviousRecommendations } from '@/hooks/api/usePreviousRecommendations';
import { formatDate } from '@/lib/formatters';

interface PreviousRecommendationSectionProps {
  recommendation: PreviousRecommendations | null | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}

export function PreviousRecommendationSection({
  recommendation,
  isLoading,
  error,
  onRetry,
}: PreviousRecommendationSectionProps) {
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Previous Recommendation</h2>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <div key={index} className="h-64 animate-pulse rounded-lg bg-muted/40" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
          <p className="text-sm text-destructive">Failed to load previous recommendations.</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : !recommendation || recommendation.products.length === 0 ? (
        <div className="rounded-lg border border-border bg-muted/20 p-6 text-sm text-muted-foreground">
          No previously recommended products were found for this opportunity.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            Forwarded on {formatDate(recommendation.forwardedAt)}
            {recommendation.forwardedBy ? ` by ${recommendation.forwardedBy}` : ''}
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {recommendation.products.map((product) => (
              <PreviousProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
